import { Suspense, useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import {
  EOB_CLASSIFICATIONS,
  FINISH_CLASSIFICATION_CATEGORIES,
  FINISH_CLASSIFICATION_CATEGORIES_LEGACY,
  isNonFinancialEob,
  useSubcategories,
} from '~/entities/categories'
import { getConsignmentUrl, US_ONLY_CONSIGNMENT_MESSAGE } from '~/entities/consignment'
import {
  DOCUMENT_SERVER_STATUS,
  getDocumentStep,
  getDocumentStepUrl,
  getDocumentsUrlWithCurrentParams,
  getDocumentUrl,
  getIsClassificationFinished,
  getIsClusteringFinished,
  getIsDataExtractionFinished,
  getIsDuplicationFinished,
  getIsEobProcessingFinished,
  getIsPatientFinished,
  getIsPaymentDepositFinished,
  getIsTaggingFinished,
  getIsTaggingStepReviewed,
  isTaggingClassification,
  useClassificationReviews,
  useCurrentDocument,
} from '~/entities/document'
import {
  FLAG_TYPES,
  getActiveFlagByType,
  getStepperFlagSeverity,
  hasActiveFlagOnStep,
  useDocumentFlags,
} from '~/entities/document-flags'
import { useUserStore } from '~/entities/user'
import {
  AuditTrailButton,
  useAuditTrailQueryParams,
  useResetAuditTrailOnUnmount,
} from '~/features/audit-trail'
import { CheckDocumentAssigneeDialog } from '~/features/check-document-assignee'
import { DocumentAgentLink } from '~/features/document-agent-link'
import { ToggleOnshoreOnly } from '~/features/toggle-onshore-only'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { ResourceLayout, ResourceLoadingSkeleton } from '~/shared/ui'
import { DocumentCard } from './document-card'
import { LoadingSkeleton } from './loading-skeleton'

function DocumentPageLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const {
    data: { document },
  } = useCurrentDocument()
  useResetAuditTrailOnUnmount()
  const { data: flagsData } = useDocumentFlags()
  const queryParams = useAuditTrailQueryParams()
  const isOnshore = useUserStore((state) => state.isOnshore)
  const isConsignmentLocked = !isOnshore && !!document.onshore_only
  const subcategories = useSubcategories()
  const {
    data: { items: classificationReviews },
  } = useClassificationReviews({ documentKey: document.key })
  const isReviewStateResetEnabled = useIsFeatureEnabled('2026-07-review-state-reset')
  const wasReclassified = (classificationReviews?.length ?? 0) >= 2
  const isFullyReviewed =
    document.review_status === DOCUMENT_SERVER_STATUS.reviewed ||
    document.review_status === DOCUMENT_SERVER_STATUS.auto_reviewed
  const currentStep = getDocumentStep(document, subcategories)
  const forceStepsNotFinished =
    isReviewStateResetEnabled && wasReclassified && !isFullyReviewed && currentStep === 'finished'
  const isTaggingEnabled = isTaggingClassification(document.category, subcategories)
  const isEobEnabled = useIsFeatureEnabled('2026-02-eob-phase-1')
  const isEobClassification =
    isEobEnabled &&
    EOB_CLASSIFICATIONS.includes(document.category as (typeof EOB_CLASSIFICATIONS)[number])

  useEffect(() => {
    if (location.pathname !== `/documents/${document.key}`) {
      return
    }

    const url = getDocumentUrl(document, subcategories)
    navigate(url, { replace: true })
  }, [document, location.pathname, navigate, subcategories])

  const stepUrlOptions = useMemo(
    () => ({
      queryParams,
      queryParamsToOmit: [queryParams.audit !== 'true' ? 'audit' : ''],
    }),
    [queryParams],
  )

  const stepsConfig = useMemo(() => {
    const clusteringStep = {
      route: getConsignmentUrl(document.cns_key, {
        queryParams: {
          ...queryParams,
          navigated_from_id: document.key,
          navigated_from_step: getDocumentStep(document, subcategories),
        },
        queryParamsToOmit: [queryParams.audit !== 'true' ? 'audit' : ''],
      }),
      label: 'Clustering',
      accessible: true,
      finished: true,
      lockedReason: isConsignmentLocked ? US_ONLY_CONSIGNMENT_MESSAGE : undefined,
    }

    const classificationStep = {
      route: getDocumentStepUrl(document.key, 'classification', stepUrlOptions),
      label: 'Classification',
      accessible: getIsClusteringFinished(document, subcategories),
      finished: getIsClassificationFinished(document, subcategories),
      flagged: hasActiveFlagOnStep(flagsData.items, 'classification'),
      flagSeverity: 'low' as const,
    }

    // Non-financial EOB documents skip Payment Deposit
    if (isEobClassification && isNonFinancialEob(document.category)) {
      return [
        clusteringStep,
        classificationStep,
        {
          route: getDocumentStepUrl(document.key, 'eob_duplication', stepUrlOptions),
          label: 'Duplication',
          accessible: getIsClassificationFinished(document, subcategories),
          finished: getIsDuplicationFinished(document, subcategories),
          flagged: hasActiveFlagOnStep(flagsData.items, 'eob_duplication'),
          flagSeverity: 'low' as const,
        },
        {
          route: getDocumentStepUrl(document.key, 'eob_processing', stepUrlOptions),
          label: 'EOB Processing',
          accessible: getIsDuplicationFinished(document, subcategories),
          finished: getIsEobProcessingFinished(document, subcategories),
          flagged: hasActiveFlagOnStep(flagsData.items, 'eob_processing'),
          flagSeverity:
            getStepperFlagSeverity(flagsData.items) ??
            (hasActiveFlagOnStep(flagsData.items, 'eob_processing') ? ('low' as const) : undefined),
        },
      ]
    }

    // Financial EOB documents get the full step sequence
    if (isEobClassification) {
      return [
        clusteringStep,
        classificationStep,
        {
          route: getDocumentStepUrl(document.key, 'eob_payment_deposit', stepUrlOptions),
          label: 'Payment Deposit',
          accessible: getIsClassificationFinished(document, subcategories),
          finished: getIsPaymentDepositFinished(document, subcategories),
          flagged: hasActiveFlagOnStep(flagsData.items, 'eob_payment_deposit'),
          flagSeverity: getActiveFlagByType(flagsData.items, FLAG_TYPES.CREDIT_CARD)
            ? ('high' as const)
            : ('low' as const),
        },
        {
          route: getDocumentStepUrl(document.key, 'eob_duplication', stepUrlOptions),
          label: 'Duplication',
          accessible: getIsPaymentDepositFinished(document, subcategories),
          finished: getIsDuplicationFinished(document, subcategories),
          flagged: hasActiveFlagOnStep(flagsData.items, 'eob_duplication'),
          flagSeverity: 'low' as const,
        },
        {
          route: getDocumentStepUrl(document.key, 'eob_processing', stepUrlOptions),
          label: 'EOB Processing',
          accessible: getIsDuplicationFinished(document, subcategories),
          finished: getIsEobProcessingFinished(document, subcategories),
          flagged: hasActiveFlagOnStep(flagsData.items, 'eob_processing'),
          flagSeverity:
            getStepperFlagSeverity(flagsData.items) ??
            (hasActiveFlagOnStep(flagsData.items, 'eob_processing') ? ('low' as const) : undefined),
        },
      ]
    }

    // Non-EOB documents
    const finishCategories = isEobEnabled
      ? FINISH_CLASSIFICATION_CATEGORIES
      : FINISH_CLASSIFICATION_CATEGORIES_LEGACY
    const isFinishCategory = finishCategories.includes(document.category ?? '')

    const baseSteps: Array<{
      route: string
      label: string
      accessible: boolean
      finished: boolean
      flagged?: boolean
      flagSeverity?: 'high' | 'low'
      lockedReason?: string
    }> = [
      clusteringStep,
      classificationStep,
      {
        route: getDocumentStepUrl(document.key, 'data', stepUrlOptions),
        label: 'Data Extraction',
        accessible: getIsClassificationFinished(document, subcategories) && !isFinishCategory,
        finished: forceStepsNotFinished
          ? false
          : getIsDataExtractionFinished(document, subcategories),
        flagged: hasActiveFlagOnStep(flagsData.items, 'data'),
        flagSeverity: 'low' as const,
      },
    ]

    if (isTaggingEnabled) {
      baseSteps.push({
        route: getDocumentStepUrl(document.key, 'carc_rarc', stepUrlOptions),
        label: 'Tagging',
        accessible: getIsDataExtractionFinished(document, subcategories) && !isFinishCategory,
        finished: forceStepsNotFinished
          ? false
          : getIsTaggingFinished(document, subcategories) || getIsTaggingStepReviewed(document),
        flagged: hasActiveFlagOnStep(flagsData.items, 'carc_rarc'),
        flagSeverity: 'low' as const,
      })
    }

    baseSteps.push({
      route: getDocumentStepUrl(document.key, 'patient', stepUrlOptions),
      label: 'Matching',
      accessible:
        (isTaggingEnabled
          ? getIsTaggingFinished(document, subcategories) || getIsTaggingStepReviewed(document)
          : getIsDataExtractionFinished(document, subcategories)) && !isFinishCategory,
      finished: forceStepsNotFinished ? false : getIsPatientFinished(document, subcategories),
      flagged: hasActiveFlagOnStep(flagsData.items, 'patient'),
      flagSeverity: 'low' as const,
    })

    return baseSteps
  }, [
    document,
    isConsignmentLocked,
    queryParams,
    stepUrlOptions,
    isEobClassification,
    isEobEnabled,
    isTaggingEnabled,
    forceStepsNotFinished,
    flagsData.items,
    subcategories,
  ])

  const activeStepIndex = useMemo(
    () =>
      stepsConfig.findIndex((step) => {
        const lastStepPathSegment = step.route?.split('?')[0].split('/').pop()
        if (!lastStepPathSegment) {
          return false
        }
        return location.pathname.includes(lastStepPathSegment)
      }),
    [stepsConfig, location.pathname],
  )

  const steps = useMemo(() => {
    return stepsConfig.map((step, index) => ({
      ...step,
      accessible: step.accessible || index <= activeStepIndex,
      finished: step.finished || index < activeStepIndex,
    }))
  }, [stepsConfig, activeStepIndex])

  return (
    <ResourceLayout
      steps={steps}
      activeStepIndex={activeStepIndex}
      backUrl={getDocumentsUrlWithCurrentParams()}
      actions={
        <>
          <ToggleOnshoreOnly />
          <DocumentAgentLink documentKey={document.key} />
          <AuditTrailButton />
        </>
      }
      assetCard={<DocumentCard document={document} />}
    >
      <Suspense fallback={<LoadingSkeleton />}>
        <Outlet />
      </Suspense>
    </ResourceLayout>
  )
}

export function DocumentPage() {
  return (
    <Suspense
      fallback={
        <ResourceLoadingSkeleton>
          <LoadingSkeleton />
        </ResourceLoadingSkeleton>
      }
    >
      <CheckDocumentAssigneeDialog />
      <DocumentPageLayout />
    </Suspense>
  )
}
